import type { TakePosition } from "../app/slices/EnvironmentSlice";
import type { Prop } from "../models/Prop";
import type { UsedProp } from "../models/UsedProp";
import type Take3D from "../models/Take3D";
import { PropType } from "../models/Prop";
import type { Actor } from "../models/Actor";

function resolveStartingProp(take: Take3D, positions: TakePosition[], prop: Prop) {
  const location = positions.find((p) => p.id === take.interactionPointId);
  if (!location) return { error: "The Take location is no longer available." };
  if (prop.link || prop.propType !== PropType.Default)
    return { error: "The starting Prop must be a direct project Prop." };
  if (take.actors.some((actor) => actor.prop === prop.id))
    return { error: "Built-in held Props cannot be used as the starting view." };
  const placement = take.props.find((p) => p.instanceId === prop.id);
  const propPosition = location.prop_positions?.find((p) => p.id === placement?.positionId);
  const attachment = take.attachments?.[prop.id];
  if (attachment) {
    const actor = take.actors.find((a) => a.actorId === attachment.id);
    if (!actor) return { error: "The starting Prop's attachment actor is not present in this Take." };
    const actorPosition = location.actor_positions?.find((p) => p.id === actor.positionId);
    if (!actorPosition) return { error: "The starting Prop's attachment actor must be at this Take location." };
    if (attachment.playerToProp && !propPosition)
      return { error: "The starting Prop must be placed directly at this Take location for an Actor to Prop attachment." };
    const position = attachment.playerToProp ? propPosition! : {
      ...actorPosition, direction: actor.direction ?? actorPosition.direction,
    };
    return { position, location, attachment };
  }
  if (!propPosition) return { error: "The starting Prop must be placed directly at this Take location." };
  return { position: propPosition, location };
}

export function getTakeStartProps(take: Take3D, positions: TakePosition[], props: Prop[]) {
  return props.filter((prop) => !resolveStartingProp(take, positions, prop).error);
}

export function getTakeStartingViewErrors(
  take: Take3D, positions: TakePosition[], props: Prop[], usedProps: Record<string, UsedProp>,
): string[] {
  if (take.startViewMode !== "propInspect") return [];
  const selection = take.startPropInspect;
  if (!selection?.propId) return ["Select a Prop for the starting view."];
  const prop = props.find((p) => p.id === selection.propId);
  if (!prop) return ["The starting Prop must be placed directly at this Take location."];
  const resolution = resolveStartingProp(take, positions, prop);
  if (resolution.error) return [resolution.error];
  const metadata = usedProps[prop.propId]?.metadata;
  if (!metadata) return ["Starting Prop metadata is unavailable. Reload the training to try again."];
  if (!selection.tag) return ["Select an Inspect view for the starting Prop."];
  if (!metadata.inspectDefinitions?.some((definition) => definition.name === selection.tag))
    return ["The selected Inspect view is no longer available on this Prop."];
  return [];
}

/** Shared dropdown/map candidates, with authoritative placement and metadata. */
export function getTakeStartPropCandidates(
  take: Take3D, positions: TakePosition[], props: Prop[], usedProps: Record<string, UsedProp>,
) {
  return getTakeStartProps(take, positions, props).flatMap((prop) => {
    const usedProp = usedProps[prop.propId];
    if (!usedProp?.metadata?.inspectDefinitions?.length) return [];
    const { position, location, attachment } = resolveStartingProp(take, positions, prop);
    return position ? [{ prop, position, usedProp, takeColor: location?.take_color,
      ...(attachment ? { attachment } : {}),
    }] : [];
  });
}

export function getTakeStartPropLabel(prop: Prop, attachment: { id: string } | undefined, actors: Actor[]) {
  return attachment ? `${prop.name} · ${actors.find((a) => a.id === attachment.id)?.workingName ?? attachment.id}` : prop.name;
}

/** Reselecting the current target must preserve its view and avoid an undo entry. */
export function withTakeStartingProp(take: Take3D, propId: string): Take3D {
  if (take.startPropInspect?.propId === propId) return take;
  return { ...take, startPropInspect: { propId, tag: "" } };
}
